import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSignedUrls } from './useCameraFrames';
import { closestIndex, mergeTimeline } from '../../lib/frames';

export const TIMELAPSE_RANGES = [
  { key: '1H', ms: 3_600_000 },
  { key: '6H', ms: 6 * 3_600_000 },
  { key: '24H', ms: 24 * 3_600_000 },
  { key: '7D', ms: 7 * 24 * 3_600_000 },
];

export const TIMELAPSE_SPEEDS = [1, 2, 4, 8]; // frames per second

// How many steps ahead of the one showing get signed and fetched. Enough to
// keep the fastest speed fed on an ordinary connection, few enough that
// pausing doesn't leave a pile of requests in flight.
const LOOKAHEAD = 6;

// Captures the cameras made this close together count as the same moment.
// They fire on the same schedule but not in the same second.
const SAME_MOMENT_MS = 60_000;

const EMPTY = [];

/**
 * A client-side slideshow over the cameras' stored stills, played side by
 * side: one timeline of capture moments inside the chosen range (every
 * camera's captures merged, see lib/frames.js), stepped oldest to newest at
 * a fixed rate, with each panel showing the frame its camera has closest to
 * the current moment. Display only -- it builds nothing, stores nothing, and
 * never asks the device for a capture; it just swaps which existing images
 * are on screen.
 *
 * Signed URLs last fifteen minutes, so a long range can't be signed up
 * front. Instead only the current step and a short lookahead are signed
 * (through useSignedUrls) and fetched into the browser cache, and playback
 * holds on a step until every camera's next frame is actually ready rather
 * than skipping or flashing blank. As the cursor moves the window slides
 * with it.
 *
 * The cursor is a capture time, so the listings can grow underneath a
 * running timelapse without it jumping. Playback stops on the newest
 * moment; play from there starts over.
 *
 * @param {{ cameraId: number, photo: Frame[] }[]} cameras
 * @param {number} nowMs
 * @param {boolean} enabled false hides the timelapse (video mode) without
 *   forgetting its settings
 */
export function useTimelapse(cameras, nowMs, enabled) {
  const [on, setOn] = useState(false);
  const [rangeKey, setRangeKey] = useState('24H');
  const [fps, setFps] = useState(2);
  const [playing, setPlaying] = useState(false);
  const [cursor, setCursor] = useState(null);

  const active = on && enabled;
  const range = TIMELAPSE_RANGES.find((r) => r.key === rangeKey) || TIMELAPSE_RANGES[0];

  // The window's start is rounded to the minute: it moves with the clock so
  // "last 24h" stays true over a long watch, but not four times a second.
  const sinceMs = Math.floor(nowMs / 60_000) * 60_000 - range.ms;

  // Per camera, the stills in range; and across cameras, the moments.
  const inRange = useMemo(
    () =>
      active
        ? cameras.map((cam) => ({
            cameraId: cam.cameraId,
            frames: cam.photo.filter((f) => f.takenAt.getTime() >= sinceMs),
          }))
        : EMPTY,
    [active, cameras, sinceMs]
  );
  const moments = useMemo(
    () => mergeTimeline(inRange.map((c) => c.frames), SAME_MOMENT_MS),
    [inRange]
  );

  const index = useMemo(() => {
    if (!moments.length) return 0;
    if (cursor == null) return 0;
    return closestIndex(moments, cursor, (t) => t);
  }, [moments, cursor]);

  // What each camera shows at a given step.
  const framesAt = useCallback(
    (i) => {
      const t = moments[i];
      const out = {};
      for (const cam of inRange) {
        const j = t == null ? -1 : closestIndex(cam.frames, t);
        out[cam.cameraId] = j >= 0 ? cam.frames[j] : null;
      }
      return out;
    },
    [moments, inRange]
  );

  const lookahead = useMemo(() => {
    if (!active) return EMPTY;
    const names = [];
    for (let i = index; i < Math.min(moments.length, index + LOOKAHEAD); i += 1) {
      for (const f of Object.values(framesAt(i))) if (f) names.push(f.filename);
    }
    return names;
  }, [active, moments.length, index, framesAt]);
  const urls = useSignedUrls(lookahead, active);

  // Warm the browser cache for the lookahead and remember which URLs have
  // arrived, so the tick below only advances onto a step that will paint at
  // once. A failed load counts as ready: better to skip past a bad frame
  // than to stall the whole run on it.
  const readyRef = useRef(new Set());
  const loadingRef = useRef(new Map());
  useEffect(() => {
    for (const filename of lookahead) {
      const url = urls[filename];
      if (!url || readyRef.current.has(url) || loadingRef.current.has(url)) continue;

      const img = new Image();
      img.onload = img.onerror = () => {
        readyRef.current.add(url);
        loadingRef.current.delete(url);
      };
      img.src = url;
      loadingRef.current.set(url, img);
    }
  }, [lookahead, urls]);

  // The tick reads the latest moments/urls through a ref rather than being
  // re-created on every change: an interval that restarts each time a URL
  // lands would never reach the end of its period.
  const advanceRef = useRef(() => {});
  advanceRef.current = () => {
    if (index + 1 >= moments.length) {
      setPlaying(false);
      return;
    }

    const next = Object.values(framesAt(index + 1)).filter(Boolean);
    const ready = next.every((f) => urls[f.filename] && readyRef.current.has(urls[f.filename]));
    if (!ready) return;
    setCursor(moments[index + 1]);
  };

  useEffect(() => {
    if (!active || !playing) return;

    const timer = setInterval(() => advanceRef.current(), 1000 / fps);
    return () => clearInterval(timer);
  }, [active, playing, fps]);

  // Switching on starts a run from the oldest moment in range: the point of
  // pressing it is to watch one.
  const toggle = useCallback(() => {
    if (on) {
      setPlaying(false);
    } else {
      setCursor(null);
      setPlaying(true);
    }
    setOn(!on);
  }, [on]);

  const play = useCallback(() => {
    if (index >= moments.length - 1) setCursor(moments[0] ?? null);
    setPlaying(true);
  }, [moments, index]);

  const pause = useCallback(() => setPlaying(false), []);

  const seek = useCallback(
    (i) => {
      const t = moments[Math.max(0, Math.min(moments.length - 1, i))];
      if (t != null) setCursor(t);
    },
    [moments]
  );

  // Stepping by hand pauses: an arrow press is a decision to look at a frame.
  const step = useCallback(
    (delta) => {
      setPlaying(false);
      seek(index + delta);
    },
    [seek, index]
  );

  const frames = useMemo(() => {
    const out = {};
    for (const [id, f] of Object.entries(framesAt(index))) {
      out[id] = f ? { ...f, url: urls[f.filename] || null } : null;
    }
    return out;
  }, [framesAt, index, urls]);

  return {
    active,
    on,
    toggle,
    rangeKey,
    setRange: setRangeKey,
    fps,
    setFps,
    playing,
    play,
    pause,
    seek,
    step,
    index,
    count: moments.length,
    // Keyed by camera id: the frame each panel shows at this step, with its
    // signed url once it has one.
    frames,
  };
}
